'use client';
import React from 'react';

import { type CurrencyCode } from '@/modules/currencies/constants';
import { type ModuleName, TileType } from '@/modules/impressions/types';
import { useImpressionsBatcher } from '@/modules/impressions/useImpressionsBatcher';
import {
  getSearchListProductPropsDebugInfo,
  isSearchListProductProps,
  type SearchListProductProps,
} from '@/modules/product/helpers/isSearchListProductProps';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  children: React.ReactNode;
  index: number;
  isLastItem: boolean;
  pageNumber: number;
  moduleName?: ModuleName;
};

/**
 * When combined with ImpressionsBatcherProvider, this component can wrap
 * Product Card type components in order to send events. This component must
 * wrap a single child whose props conform to SearchListProductProps.
 */
export function ProductImpressionHandler({
  children,
  index,
  isLastItem,
  pageNumber,
  moduleName,
}: Props) {
  const child = React.Children.only(
    children
  ) as React.ReactElement<SearchListProductProps>;

  if (!child) {
    throw new Error('No child passed to ProductImpressionHandler');
  }

  if (!isSearchListProductProps(child.props)) {
    const debug = getSearchListProductPropsDebugInfo(child.props);
    throw new Error(
      `Child component of ProductImpressionHandler must conform to SearchListProductProps. Debug info: ${JSON.stringify(debug)}`
    );
  }

  const [isAuthenticated] = useCurrentUser();

  const shouldCollectImpression = isAuthenticated;

  /**
   * Backend expects a 1-based index
   */
  const indexPosition = index + 1;

  const {
    id,
    isBoosted,
    pricing: { currency, original_price, discounted_price },
  } = child.props;

  const { impressionRef } = useImpressionsBatcher({
    shouldCollectImpression,
    productId: id,
    tileType: isBoosted ? TileType.Boosted : TileType.Organic,
    position: indexPosition,
    page: pageNumber,
    prices: {
      currency: currency as CurrencyCode,
      originalPrice: original_price.price_breakdown.price.amount,
      discountedPrice: discounted_price?.price_breakdown.price.amount,
      taxInclusiveOriginalPrice: original_price.total_price,
      taxInclusiveDiscountedPrice: discounted_price?.total_price,
    },
    moduleName,
    isEnd: isLastItem,
  });

  const productCardRef = shouldCollectImpression ? impressionRef : null;

  return <div ref={productCardRef}>{child}</div>;
}
